import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/i18n/LanguageContext";
import { useLocation, Link } from "wouter";

interface PuzzleDetailsTabProps {
  puzzle: {
    id: number;
    title: string;
    description: string;
    address: string;
    prize: string;
    createdAt: string;
    userId: number;
    solved: boolean;
    imageUrl?: string;
    solvedByUser?: {
      id: number;
      displayName?: string;
      email: string;
      avatarUrl?: string;
    };
    user: {
      id: number;
      displayName?: string;
      email: string;
      avatarUrl?: string;
    };
  };
  user: {
    id: number;
    email: string;
    displayName?: string;
    avatarUrl?: string;
    cacatais: number;
  } | undefined;
  setIsImageModalOpen: (isOpen: boolean) => void;
  openSolveDialog: () => void;
}

export function PuzzleDetailsTab({ 
  puzzle, 
  user, 
  setIsImageModalOpen, 
  openSolveDialog 
}: PuzzleDetailsTabProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  return (
    <div className="space-y-6">
      {puzzle.imageUrl && (
        <div>
          <img 
            src={puzzle.imageUrl} 
            alt={puzzle.title}
            className="w-full max-h-96 object-contain rounded-md cursor-pointer"
            onClick={() => setIsImageModalOpen(true)}
          />
        </div>
      )}
      
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">{t("puzzles.description")}</h3>
        <p className="text-zinc-300 whitespace-pre-line break-words overflow-hidden">{puzzle.description}</p>
      </div>
      
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">{t("puzzles.bitcoinAddress")}</h3>
        <code className="block bg-zinc-950 p-3 rounded text-sm font-mono break-all text-zinc-300">
          {puzzle.address}
        </code>
      </div>
      
      <div className="flex items-center p-3 bg-zinc-800 rounded-md">
        {puzzle.user.avatarUrl ? (
          <img 
            src={puzzle.user.avatarUrl} 
            alt="Creator" 
            className="w-8 h-8 rounded-full mr-3"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center mr-3">
            <span className="text-sm text-white">
              {puzzle.user.displayName?.charAt(0) || puzzle.user.email.charAt(0)}
            </span>
          </div>
        )}
        <div>
          <Link 
            href={`/users/${puzzle.user.displayName ? puzzle.user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : puzzle.user.id}`}
            className="text-sm text-zinc-300 hover:text-blue-400 hover:underline transition-colors"
          >
            {t("desafios_cacatais.created_by", { name: puzzle.user.displayName || puzzle.user.email.split('@')[0], fallback: `Created by ${puzzle.user.displayName || puzzle.user.email.split('@')[0]}` })}
          </Link>
        </div>
      </div>
      
      {puzzle.solved && puzzle.solvedByUser && (
        <div className="flex items-center p-3 bg-zinc-800 rounded-md">
          {puzzle.solvedByUser.avatarUrl ? (
            <img 
              src={puzzle.solvedByUser.avatarUrl} 
              alt="Solver" 
              className="w-8 h-8 rounded-full mr-3"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center mr-3">
              <span className="text-sm text-white">
                {puzzle.solvedByUser.displayName?.charAt(0) || puzzle.solvedByUser.email.charAt(0)}
              </span>
            </div>
          )}
          <div>
            <Link 
              href={`/users/${puzzle.solvedByUser.displayName ? puzzle.solvedByUser.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : puzzle.solvedByUser.id}`}
              className="text-sm text-zinc-300 hover:text-blue-400 hover:underline transition-colors"
            >
              {t("desafios_cacatais.solved_by", { name: puzzle.solvedByUser.displayName || puzzle.solvedByUser.email.split('@')[0] })}
            </Link>
          </div>
        </div>
      )}
      
      <div className="flex flex-wrap justify-end gap-2 sm:gap-3 pt-4">
        <Button 
          variant="outline" 
          onClick={() => {
            navigator.clipboard.writeText(puzzle.address);
            toast({ title: t("desafios_cacatais.address_copied") });
          }}
        >
          {t("desafios_cacatais.copy_address")}
        </Button>
        
        {user && user.id === puzzle.userId && !puzzle.solved && (
          <>
            <Button 
              variant="outline"
              onClick={() => setLocation(`/puzzles/edit/${puzzle.id}`)}
            >
              {t("desafios_cacatais.edit_puzzle")}
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => setLocation(`/puzzles/custom/${puzzle.id}/hints`)}
            >
              {t("desafios_cacatais.sell_hints")}
            </Button>
          </>
        )}
        
        {!puzzle.solved && (
          <Button onClick={openSolveDialog}>
            {t("desafios_cacatais.try_solution")}
          </Button>
        )}
      </div>
    </div>
  );
}
